import {
  Paper,
  Stack,
  Text,
  Badge,
  Group,
  Collapse,
  ActionIcon,
  NumberInput,
  Button,
  Select,
  Modal,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  isSelectableStainCode,
  type StainSelectionContext,
} from "../../utils/controlStainSelection";
import { useSourceDefaultStainsForPreview, useStains } from "../../hooks";
import type { Stain } from "../../services/admin/stains.service";
import type { SourceDefaultStain } from "../../services/sourceDefaultStains.service";
import type { CaseType } from "../../utils/deriveCaseType";
import {
  buildStainSelectionId,
  buildActiveStainOptions,
  deduplicatePreviewStains,
  getStainSelectionId,
  shouldFetchSourceDefaultStains,
} from "../../utils/resolvedStainSelections";
import { allowsZeroTechnicalMaterial } from "../../utils/grossingOnlyPolicy";
import { createUuid } from "../../utils/createUuid";
import StainOccurrenceRow, { type StainOccurrence } from "./StainOccurrenceRow";
import PanelOrReflexSelector from "./PanelOrReflexSelector";
import type {
  AccessioningPanelOption,
  AccessioningPanelSelection,
} from "../../services/accessioningPanels.service";

const EMPTY_SOURCE_DEFAULT_STAINS: SourceDefaultStain[] = [];
const EMPTY_STAINS: Stain[] = [];

// Utility to filter out options without valid value property
// This prevents Mantine Select validation errors during navigation/hydration
const sanitizeOptions = <T extends { value: string }>(
  options: T[] | undefined | null
): T[] => {
  if (!Array.isArray(options)) return [];
  return options.filter(
    (opt): opt is T =>
      opt != null &&
      typeof opt === "object" &&
      "value" in opt &&
      opt.value != null &&
      opt.value !== ""
  );
};

// Type for manually added stain info (also used by AccessioningPage for slide label printing)
export interface StainInfo {
  id: string;
  selectionId?: string;
  code: string;
  description: string;
  markAsCompleted?: boolean;
  isSystemGenerated?: boolean;
  isPersisted?: boolean;
  panelSelectionKey?: string;
  panelComponentId?: string;
  panelName?: string;
}

// Type for manual stains state per specimen
export interface ManualStainsState {
  added: StainInfo[];
  removed: string[];
}

export interface StainRemovalTarget {
  stainId: string;
  selectionId: string;
}

export function areStainInfoOccurrencesEqual(
  left: StainInfo[] | undefined,
  right: StainInfo[]
): boolean {
  if (!left || left.length !== right.length) return false;
  return left.every((stain, index) => {
    const other = right[index];
    return (
      stain.id === other.id &&
      stain.selectionId === other.selectionId &&
      stain.code === other.code &&
      stain.description === other.description &&
      stain.markAsCompleted === other.markAsCompleted &&
      stain.isSystemGenerated === other.isSystemGenerated &&
      stain.isPersisted === other.isPersisted &&
      stain.panelSelectionKey === other.panelSelectionKey &&
      stain.panelComponentId === other.panelComponentId &&
      stain.panelName === other.panelName
    );
  });
}

export default function SpecimenPreviewCard({
  specimen,
  specimenIndex,
  slideCompletions,
  onCompletionChange,
  onBlockCountChange,
  manualStains,
  onAddStain,
  onRemoveStain,
  accessioningType,
  practiceId,
  billingScenarioId,
  onResolvedStainsChange,
  workListStainsForSpecimen,
  assignedPrefix,
  stainSelectionContext = "ordinary",
  panelOptions,
  panelSelections,
  panelsLoading,
  onAddPanelSelection,
  onRemovePanelSelection,
}: {
  specimen: {
    uiKey?: string;
    id?: string;
    expectedSpecimenNumber?: string;
    number: string;
    sourceId?: string;
    sourceName?: string;
    subsiteId?: string;
    subsiteName?: string;
    collectionMethod?: string;
    material?: string;
    description?: string;
    blockCount?: number;
    committedFindings?: string;
    matchedStainIds?: string[];
  };
  specimenIndex: number;
  slideCompletions: Record<string, boolean>;
  onCompletionChange: (
    specimenIndex: number,
    selectionId: string,
    isCompleted: boolean
  ) => void;
  onBlockCountChange: (specimenIndex: number, count: number) => void;
  manualStains?: ManualStainsState;
  onAddStain?: (specimenIndex: number, stain: StainInfo) => void;
  onRemoveStain?: (specimenIndex: number, target: StainRemovalTarget) => void;
  accessioningType?: CaseType;
  practiceId?: string;
  billingScenarioId?: string;
  onResolvedStainsChange?: (specimenIndex: number, stains: StainInfo[]) => void;
  workListStainsForSpecimen?: Array<{
    stainId: string;
    stainCode: string;
    isCompleted: boolean;
  }>;
  assignedPrefix?: string;
  stainSelectionContext?: StainSelectionContext;
  panelOptions?: AccessioningPanelOption[];
  panelSelections?: AccessioningPanelSelection[];
  panelsLoading?: boolean;
  onAddPanelSelection?: (
    specimenIndex: number,
    option: AccessioningPanelOption
  ) => void;
  onRemovePanelSelection?: (
    specimenIndex: number,
    selectionKey: string
  ) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);

  // Remove slide confirmation modal state
  const [
    removeModalOpened,
    { open: openRemoveModal, close: closeRemoveModal },
  ] = useDisclosure(false);
  const [stainToRemove, setStainToRemove] = useState<{
    stainId: string;
    selectionId: string;
    code: string;
  } | null>(null);

  // Open remove confirmation modal
  const handleRemoveClick = useCallback(
    (stain: { stainId: string; selectionId: string; code: string }) => {
      setStainToRemove(stain);
      openRemoveModal();
    },
    [openRemoveModal]
  );

  // Confirm stain removal
  const confirmRemove = useCallback(() => {
    if (stainToRemove && onRemoveStain) {
      onRemoveStain(specimenIndex, stainToRemove);
      closeRemoveModal();
      setStainToRemove(null);
    }
  }, [closeRemoveModal, onRemoveStain, specimenIndex, stainToRemove]);

  const handleCollapseToggle = useCallback(() => {
    setCollapsed((current) => !current);
  }, []);

  const handleBlockCountChange = useCallback(
    (value: string | number) => {
      onBlockCountChange(specimenIndex, typeof value === "number" ? value : 1);
    },
    [onBlockCountChange, specimenIndex]
  );

  const handleOccurrenceCompletionChange = useCallback(
    (selectionId: string, isCompleted: boolean) => {
      onCompletionChange(specimenIndex, selectionId, isCompleted);
    },
    [onCompletionChange, specimenIndex]
  );

  const hasWorkListOverride = workListStainsForSpecimen !== undefined;

  const isConsultation = accessioningType === "consultation";
  const isGrossingOnly = allowsZeroTechnicalMaterial(accessioningType);

  // Only slides_only / blocks_and_slides specimens may have pre-processed (received
  // already-stained) slides — mirror of the backend completeAccessioning rule.
  const canBePreProcessed =
    specimen.material === "slides_only" ||
    specimen.material === "blocks_and_slides";

  // TC-only cases never receive source-default stain rules. Work-list and manual
  // selections remain available, and the universal H&E fallback is handled below.
  const shouldFetchDefaults = shouldFetchSourceDefaultStains(
    accessioningType,
    hasWorkListOverride
  );

  const defaultStainsQuery = useSourceDefaultStainsForPreview(
    shouldFetchDefaults ? specimen.sourceId : undefined,
    hasWorkListOverride ? undefined : specimen.committedFindings,
    hasWorkListOverride ? undefined : specimen.subsiteId,
    hasWorkListOverride ? undefined : practiceId,
    hasWorkListOverride ? undefined : billingScenarioId,
    assignedPrefix,
    canBePreProcessed
  );
  const defaultStains = defaultStainsQuery.data ?? EMPTY_SOURCE_DEFAULT_STAINS;
  const { isLoading } = defaultStainsQuery;

  // Fetch all available stains for the add dropdown
  const stainsQuery = useStains();
  const allStains = stainsQuery.data ?? EMPTY_STAINS;
  const { isLoading: isLoadingAllStains } = stainsQuery;

  // Compute the merged list of stains (defaults + added - removed)
  const mergedStains = useMemo((): StainOccurrence[] => {
    const removedIds = new Set(
      hasWorkListOverride ? [] : manualStains?.removed || []
    );
    const addedStains = hasWorkListOverride ? [] : manualStains?.added || [];

    const baseStains = hasWorkListOverride
      ? workListStainsForSpecimen.map((stain, stainIndex) => ({
          id: stain.stainId,
          selectionId: buildStainSelectionId(
            "work-list",
            specimenIndex,
            stainIndex,
            stain.stainId
          ),
          code: stain.stainCode,
          description: stain.stainCode,
          markAsCompleted: stain.isCompleted,
          isDefault: false,
          isWorkList: true,
        }))
      : deduplicatePreviewStains(
          defaultStains.map((stain) => ({
            id: stain.stainId,
            source: stain,
            markAsCompleted: stain.markAsCompleted,
          }))
        ).map(({ source: stain }, stainIndex) => ({
          id: stain.stainId,
          selectionId: buildStainSelectionId(
            "default",
            specimenIndex,
            stainIndex,
            stain.stainId
          ),
          code: stain.stainCode,
          description: stain.stainName,
          markAsCompleted: stain.markAsCompleted,
          isDefault: true,
          isWorkList: false,
        }));
    const hasPersistedOccurrences = addedStains.some(
      (stain) => stain.isPersisted
    );
    const baseFiltered = hasPersistedOccurrences
      ? []
      : baseStains.filter((stain) => !removedIds.has(stain.id));

    const manualAdded = addedStains.map((s, stainIndex) => ({
      id: s.id,
      selectionId:
        s.selectionId ??
        buildStainSelectionId("legacy-manual", specimenIndex, stainIndex, s.id),
      code: s.code,
      description: s.description,
      markAsCompleted: false,
      isDefault: false,
      isWorkList: false,
    }));

    const panelOccurrences = (panelSelections || []).flatMap((selection) => {
      const option = (panelOptions || []).find(
        (candidate) =>
          candidate.id === selection.optionId &&
          candidate.optionType === selection.optionType
      );
      if (!option) return [];
      return option.components.flatMap((component) =>
        Array.from({ length: component.quantity }, (_, quantityIndex) => ({
          id: component.stainId,
          selectionId: `${selection.selectionKey}:${component.id}:${
            quantityIndex + 1
          }`,
          code: component.stainCode,
          description: component.stainDescription,
          markAsCompleted: false,
          isDefault: false,
          isWorkList: false,
          panelSelectionKey: selection.selectionKey,
          panelComponentId: component.id,
          panelName: option.name,
        }))
      );
    });

    const combined = isGrossingOnly
      ? []
      : [...baseFiltered, ...manualAdded, ...panelOccurrences];

    // C-4746: Ensure H&E is ALWAYS present unless explicitly removed by the user.
    // This prevents manual stain addition from "deleting" the mandatory H&E slide.
    const heStain = allStains.find((s) => s.code === "H&E");
    const hasBaseHE = baseFiltered.some((s) => s.id === heStain?.id);
    const isHeRemoved = heStain && removedIds.has(heStain.id);

    if (
      !hasWorkListOverride &&
      !isGrossingOnly &&
      !hasPersistedOccurrences &&
      heStain &&
      !hasBaseHE &&
      !isHeRemoved
    ) {
      combined.push({
        id: heStain.id,
        selectionId: buildStainSelectionId(
          "fallback",
          specimenIndex,
          0,
          heStain.id
        ),
        code: heStain.code,
        description: heStain.description,
        markAsCompleted: false,
        isDefault: true,
        isWorkList: false,
      });
    }

    // For consultation: slides come from the document. If none were extracted/added,
    // fall back to 1 H&E slide — mirrors the backend's mergeStainsWithManual fallback.
    if (isConsultation && combined.length === 0) {
      const he = allStains.find((s) => s.code === "H&E");
      if (he) {
        combined.push({
          id: he.id,
          selectionId: buildStainSelectionId(
            "fallback",
            specimenIndex,
            0,
            he.id
          ),
          code: he.code,
          description: he.description,
          markAsCompleted: true,
          isDefault: false,
          isWorkList: false,
        });
      }
    }

    // The backend always creates at least one slide (defaulting to H&E). For
    // slides_only / blocks_and_slides specimens source defaults are skipped, so
    // the list can be empty here. Default to a single H&E slide so the preview's
    // slide count never falls below 1 and matches what the backend will create.
    if (combined.length === 0 && !hasWorkListOverride && !isGrossingOnly) {
      const he =
        allStains.find((s) => s.stainType === "H&E") ??
        allStains.find((s) => s.code === "H&E");
      if (he) {
        return [
          {
            id: he.id,
            selectionId: buildStainSelectionId(
              "fallback",
              specimenIndex,
              0,
              he.id
            ),
            code: he.code,
            description: he.description ?? "H&E",
            markAsCompleted: false,
            isDefault: true,
            isWorkList: false,
          },
        ];
      }
    }

    // H&E should always be slide 1
    combined.sort((a, b) => {
      const aIsHE = a.code === "H&E";
      const bIsHE = b.code === "H&E";
      if (aIsHE && !bIsHE) return -1;
      if (!aIsHE && bIsHE) return 1;
      return 0;
    });
    return combined;
  }, [
    defaultStains,
    manualStains,
    hasWorkListOverride,
    workListStainsForSpecimen,
    isConsultation,
    allStains,
    specimenIndex,
    panelOptions,
    panelSelections,
    isGrossingOnly,
  ]);

  const handleAddPanel = useCallback(
    (option: AccessioningPanelOption) => {
      onAddPanelSelection?.(specimenIndex, option);
    },
    [onAddPanelSelection, specimenIndex]
  );
  const handleRemovePanel = useCallback(
    (selectionKey: string) => {
      onRemovePanelSelection?.(specimenIndex, selectionKey);
    },
    [onRemovePanelSelection, specimenIndex]
  );

  // Lift resolved stains to parent for slide label printing (C-784)
  useEffect(() => {
    if (onResolvedStainsChange) {
      onResolvedStainsChange(
        specimenIndex,
        mergedStains.map((s) => ({
          id: s.id,
          selectionId: s.selectionId,
          code: s.code,
          description: s.description,
          markAsCompleted: s.markAsCompleted,
          panelSelectionKey: s.panelSelectionKey,
          panelComponentId: s.panelComponentId,
          panelName: s.panelName,
        }))
      );
    }
  }, [mergedStains, specimenIndex, onResolvedStainsChange]);

  // Auto-add and auto-mark stains as completed for pass-through and consultation
  // cases when matchedStainIds are available from AI extraction. Consultation
  // cases receive pre-made slides, so all matched stains are marked completed.
  // Only runs once per specimen to avoid overriding subsequent user toggles.
  const processedConfigRef = useRef({ type: "", ids: "" });
  useEffect(() => {
    const currentType = accessioningType || "";
    const currentIds = specimen.matchedStainIds?.join(",") || "";

    if (
      processedConfigRef.current.type === currentType &&
      processedConfigRef.current.ids === currentIds
    ) {
      return;
    }

    if (
      accessioningType !== "pass_through" &&
      accessioningType !== "consultation"
    )
      return;
    if (hasWorkListOverride) return;
    const ids = specimen.matchedStainIds;
    if (!ids || ids.length === 0) return;
    // For pass-through, default stains must be loaded first before we can merge.
    // For consultation, mergedStains starts empty (no defaults), so skip this guard.
    if (accessioningType !== "consultation" && mergedStains.length === 0)
      return;
    if (allStains.length === 0) return;

    const matchedSet = new Set(ids);
    const mergedIds = new Set(mergedStains.map((s) => s.id));

    if (onAddStain) {
      ids.forEach((stainId) => {
        if (!mergedIds.has(stainId)) {
          const stainData = allStains.find((s) => s.id === stainId);
          if (stainData) {
            const selectionId = createUuid();
            onAddStain(specimenIndex, {
              id: stainData.id,
              selectionId,
              code: stainData.code,
              description: stainData.description,
              isSystemGenerated: true,
            });
            onCompletionChange(specimenIndex, selectionId, true);
          }
        }
      });
    }

    // Identify which stains we just auto-added (to mark them completed)
    // Also find existing default stains that matched the AI extraction
    // (We only auto-complete them if they aren't already completed)
    const stainsToComplete = mergedStains.filter(
      (stain) =>
        matchedSet.has(stain.id) &&
        slideCompletions[stain.selectionId] === undefined &&
        slideCompletions[stain.id] === undefined
    );

    stainsToComplete.forEach((stain) => {
      onCompletionChange(specimenIndex, stain.selectionId, true);
    });

    processedConfigRef.current = { type: currentType, ids: currentIds };
  }, [
    mergedStains,
    specimen.matchedStainIds,
    accessioningType,
    allStains,
    hasWorkListOverride,
    onAddStain,
    onCompletionChange,
    slideCompletions,
    specimenIndex,
  ]);

  // Eligibility applies to new choices; existing physical occurrences remain visible.
  const availableStainsForDropdown = useMemo(() => {
    const removedIds = new Set(manualStains?.removed || []);

    // Start with stains from the full stains list (if available)
    const fromAllStains = buildActiveStainOptions(
      allStains.filter((stain) =>
        isSelectableStainCode(stain.code, stainSelectionContext)
      )
    );

    // Also include removed default stains (they may not be in allStains or allStains may be empty)
    const removedDefaultStains = defaultStains
      .filter(
        (s) =>
          removedIds.has(s.stainId) &&
          isSelectableStainCode(s.stainCode, stainSelectionContext)
      )
      .map((s) => ({
        value: s.stainId,
        label: `${s.stainCode} - ${s.stainName}`,
      }));

    // Combine and deduplicate by value
    const combinedMap = new Map<string, { value: string; label: string }>();
    [...fromAllStains, ...removedDefaultStains].forEach((s) => {
      if (!combinedMap.has(s.value)) {
        combinedMap.set(s.value, s);
      }
    });

    // Sanitize to ensure all options have valid value property
    return sanitizeOptions(Array.from(combinedMap.values()));
  }, [allStains, defaultStains, manualStains, stainSelectionContext]);

  // Handle adding a stain directly when selected from dropdown
  const handleStainSelected = useCallback(
    (stainId: string | null) => {
      if (!stainId || !onAddStain) return;

      // First check if it's a removed default stain
      const removedDefault = defaultStains.find((s) => s.stainId === stainId);
      if (
        removedDefault &&
        isSelectableStainCode(removedDefault.stainCode, stainSelectionContext)
      ) {
        onAddStain(specimenIndex, {
          id: removedDefault.stainId,
          code: removedDefault.stainCode,
          description: removedDefault.stainName,
        });
        return;
      }

      // Otherwise look in allStains
      const stainToAdd = allStains.find((s) => s.id === stainId);
      if (
        stainToAdd &&
        isSelectableStainCode(stainToAdd.code, stainSelectionContext)
      ) {
        onAddStain(specimenIndex, {
          id: stainToAdd.id,
          selectionId: createUuid(),
          code: stainToAdd.code,
          description: stainToAdd.description,
        });
      }
    },
    [allStains, defaultStains, onAddStain, specimenIndex, stainSelectionContext]
  );

  if (!specimen.sourceId || isLoading) {
    return null;
  }

  // Show card even if no default stains (user might add manually)
  const blockCount = isGrossingOnly ? 0 : specimen.blockCount || 1;
  const slideCount = mergedStains.length;

  return (
    <Paper p="sm" withBorder>
      <Stack gap="xs">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <Badge
              size="md"
              variant="filled"
              color="blue"
              style={{ flexShrink: 0 }}
            >
              {specimen.number}
            </Badge>
            {specimen.sourceName && (
              <Text size="sm" c="dimmed" truncate style={{ minWidth: 0 }}>
                {specimen.sourceName}
                {specimen.subsiteName ? `, ${specimen.subsiteName}` : ""}
                {specimen.collectionMethod
                  ? `, ${specimen.collectionMethod}`
                  : ""}
              </Text>
            )}
          </Group>
          <ActionIcon variant="subtle" size="sm" onClick={handleCollapseToggle}>
            {collapsed ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronUp size={16} />
            )}
          </ActionIcon>
        </Group>

        {/* Summary */}
        <Group gap="md">
          <Group gap="xs">
            <Text size="sm">
              {accessioningType === "tc_only" ||
              accessioningType === "pass_through" ||
              accessioningType === "consultation"
                ? "📦 Received Blocks:"
                : "📦 Blocks:"}
            </Text>
            {isGrossingOnly ? (
              <Text size="sm" fw={500}>
                0
              </Text>
            ) : (
              <NumberInput
                value={blockCount}
                onChange={handleBlockCountChange}
                min={1}
                max={99}
                size="xs"
                w={60}
                styles={{ input: { textAlign: "center", fontWeight: 500 } }}
              />
            )}
          </Group>
          <Text size="sm" fw={500}>
            🔬 Slides: {slideCount}
          </Text>
        </Group>

        {!isGrossingOnly && onAddPanelSelection && onRemovePanelSelection && (
          <PanelOrReflexSelector
            options={panelOptions || []}
            stainSelectionContext={stainSelectionContext}
            selections={panelSelections || []}
            isLoading={Boolean(panelsLoading)}
            onAdd={handleAddPanel}
            onRemove={handleRemovePanel}
          />
        )}

        {/* Slide Details */}
        <Collapse in={!collapsed}>
          <Stack gap="xs" mt="xs">
            {mergedStains.length === 0 ? (
              <Text
                size="xs"
                c={hasWorkListOverride ? "red.7" : "dimmed"}
                ta="center"
              >
                {hasWorkListOverride
                  ? "No work-list stain could be resolved for this specimen."
                  : "No stains configured. Add stains below."}
              </Text>
            ) : (
              mergedStains.map((stain, idx) => {
                const isCompleted =
                  slideCompletions[getStainSelectionId(stain)] ??
                  slideCompletions[stain.id] ??
                  stain.markAsCompleted;

                return (
                  <StainOccurrenceRow
                    key={stain.selectionId}
                    stain={stain}
                    slideIndex={idx}
                    isCompleted={isCompleted}
                    canBePreProcessed={canBePreProcessed}
                    canRemove={
                      Boolean(onRemoveStain) &&
                      !hasWorkListOverride &&
                      !stain.panelSelectionKey
                    }
                    removalDisabled={
                      !isGrossingOnly && mergedStains.length <= 1
                    }
                    onCompletionChange={handleOccurrenceCompletionChange}
                    onRemove={handleRemoveClick}
                  />
                );
              })
            )}

            {/* Add Stain Section */}
            {onAddStain && !hasWorkListOverride && !isGrossingOnly && (
              <Group gap="xs" mt="xs">
                {isLoadingAllStains ? (
                  <Text size="xs" c="dimmed" fs="italic">
                    Loading stains...
                  </Text>
                ) : availableStainsForDropdown.length > 0 ? (
                  <Select
                    placeholder="+ Add stain..."
                    data={availableStainsForDropdown}
                    value={null}
                    onChange={handleStainSelected}
                    size="xs"
                    searchable
                    style={{ flex: 1 }}
                    comboboxProps={{ withinPortal: true }}
                  />
                ) : (
                  <Text size="xs" c="dimmed" fs="italic">
                    All available stains added
                  </Text>
                )}
              </Group>
            )}
          </Stack>
        </Collapse>
      </Stack>

      {/* Remove Slide Confirmation Modal */}
      <Modal
        opened={removeModalOpened}
        onClose={closeRemoveModal}
        title="Remove Slide"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to remove slide{" "}
            <strong>{stainToRemove?.code}</strong> from Specimen{" "}
            {specimen.number}?
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeRemoveModal}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmRemove}>
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
